import { Property } from 'csstype';
import { DefaultTheme } from 'styled-components';
import { IconName, NotificationSeverity } from 'components/elements';
import { ConfigStorageKey } from 'types/config';
import { ThemeVariant } from 'types/styles';

/**
 * Get CSS color associated with the specified severity
 *
 * @param {DefaultTheme} theme Source theme
 * @param {Severity} severity Target severity
 */
export const getSeverityColor = (
  theme: DefaultTheme,
  severity: NotificationSeverity,
): Property.Color => {
  switch (severity) {
    case 'error':
      return theme.severity.negative;
    case 'info':
      return theme.severity.info;
    case 'success':
      return theme.severity.positive;
    case 'warning':
      return theme.severity.warning;
  }
};

/**
 * Get name of the icon associated with the specified severity
 *
 * @param {Severity} severity Target severity
 */
export const getSeverityIcon = (severity: NotificationSeverity): IconName => {
  switch (severity) {
    case 'error':
      return 'error';
    case 'info':
      return 'info';
    case 'success':
      return 'success';
    case 'warning':
      return 'warning';
  }
};

/**
 * Persist custom theme variant to local storage
 *
 * @param {ThemeVariant} variant Theme variant to persist
 */
export const writeLocalThemeVariant = (variant: ThemeVariant) => {
  const key: ConfigStorageKey = 'ui.theme';

  if (variant === 'light') {
    localStorage.removeItem(key);
  } else {
    localStorage.setItem(key, variant);
  }
};

/**
 * Get theme variant persisted in local storage
 */
export const readLocalThemeVariant = (): ThemeVariant => {
  const key: ConfigStorageKey = 'ui.theme';

  const value = localStorage.getItem(key);
  const allowed: Array<ThemeVariant> = ['dark', 'light'];
  return value !== null && (allowed as Array<string>).includes(value)
    ? (value as ThemeVariant)
    : 'light';
};
